import * as JSZip from 'jszip';
import { KostDataImportDatasetInputProcessorProgressEvent } from '@bindoc-projects/kost-data-import-domain-lib';
import {
  KOST_FILE_NAME_REG_EXP,
  KOST_FILE_NAMES,
  KOST_FILES_REQUIRED,
  KostFileNames,
} from './de-kost-file.const';

export class KostFileExtractor {
  public static async extract(
    files: {
      [fileType in KostFileNames]: JSZip.JSZipObject;
    },
    progressCallback: (
      event: KostDataImportDatasetInputProcessorProgressEvent,
    ) => void,
  ): Promise<{
    files: {
      [fileType in KostFileNames]: string;
    };
    errors: string[];
  }> {
    progressCallback({ step: 'FILE_EXTRACTING', current: 0, max: 1 });
    const reqs = KOST_FILE_NAMES.map((fileName) =>
      KostFileExtractor._extractFile(fileName, files, progressCallback),
    );

    return await Promise.all(reqs).then((fileContents) => {
      const result = fileContents.reduce(
        (
          previousValue: {
            files: { [fileType in KostFileNames]: string };
            errors: string[];
          },
          currentValue: { file?: string; error?: string },
          currentIndex,
        ) => {
          if (currentValue.error) {
            previousValue.errors.push(currentValue.error);
          } else if (!currentValue.file && !currentValue.error) {
            previousValue.files[KOST_FILE_NAMES[currentIndex]] = null;
          } else {
            previousValue.files[KOST_FILE_NAMES[currentIndex]] =
              currentValue.file;
          }

          return previousValue;
        },
        {
          files: {
            ENTGELTE: null,
            FAB: null,
            FALL: null,
            ICD: null,
            OPS: null,
            KOSTEN: null,
          },
          errors: [],
        },
      );

      progressCallback({ step: 'FILE_EXTRACTING', current: 1, max: 1 });

      return result;
    });
  }

  private static async _extractFile(
    fileNameToExtract: KostFileNames,
    files: { [key: string]: JSZip.JSZipObject },
    progressCallback: (
      event: KostDataImportDatasetInputProcessorProgressEvent,
    ) => void,
  ): Promise<{ file?: string; error?: string }> {
    const result: { file?: string; error?: string } = {};
    const filteredFileNames: string[] = Object.keys(files).filter(
      (fileName) => {
        const uppercaseFilename = fileName.toUpperCase();
        const regex = KOST_FILE_NAME_REG_EXP[fileNameToExtract];
        const match = uppercaseFilename.match(regex);
        return match !== null;
      },
    );

    if (
      (!filteredFileNames || filteredFileNames.length === 0) &&
      KOST_FILES_REQUIRED[fileNameToExtract]
    ) {
      result.error = `Datei nicht gefunden: ${fileNameToExtract}.CSV`;
    } else if (!!filteredFileNames && filteredFileNames.length > 0) {
      const fileName = filteredFileNames[0];

      // send log message to user
      progressCallback({
        step: `Datei ${fileName} für ${fileNameToExtract}.CSV erkannt`,
        current: 0,
        max: 1,
        type: { name$: 'STEP_UPDATE' },
      });
      result.file = (await files[fileName].async('binarystring')) as string;
    }
    return result;
  }
}
